// Translated
// Migrated
<template>
  <div class="trip-card-container flex flex-row relative overflow-hidden rounded-lg">
    <article
      class="flex flex-wrap sm:flex-nowrap w-full"
      @mouseover="isHovering = true"
      @mouseout="isHovering = false"
    >
      <div class="flex w-full sm:w-4/12 relative h-52 sm:h-full">
        <ResponsiveImage
          class="h-full size-full"
          img-class="rounded-tl-xl sm:rounded-none overflow-hidden"
          position="absolute"
          :image="trip?.main_picture_cloudinary"
          :alt="trip?.title || $t('siteTitleBrand')"
          lazy
          :skeleton="loading"
        >
          <div class="absolute top-0 left-0">
            <FavouriteIcon
              :id="trip?.id"
              class="!rounded-tr-none !rounded-br-lg"
            />
          </div>
        </ResponsiveImage>
      </div>
      <div class="flex justify-between text-black w-full">
        <div
          v-if="loading"
          class="w-full"
        >
          <div class="w-full">
            <b-skeleton
              as="h5"
              height="1.5rem"
              width="30%"
            />
            <b-skeleton
              as="p"
              width="60%"
            />
          </div>
          <div class="w-full">
            <b-skeleton
              class="block"
              as="span"
              width="45%"
            />
          </div>
        </div>
        <div
          v-else
          class="content-container bg-white flex flex-col justify-between relative w-full"
        >
          <div
            ref="contentContainerRef"
            class="px-4 pt-2 sm:pt-4 pb-3 sm:pb-4 sm:px-6 flex flex-col size-full"
          >
            <div
              v-if="trip?.new_trip"
              class="bg-blue-100 rounded-full text-blue-800 primary-font mr-2 text-sm/3 font-semibold px-3 py-1.5 w-fit"
            >
              {{ $t('newTrip') }}
            </div>
            <div class="flex grow flex-wrap">
              <div class="flex items-center order-1 w-full sm:w-3/4 font-primary font-bold text-lg">
                {{ trip?.title }}
              </div>
              <div
                class="hidden sm:inline-flex flex-col sm:flex-row items-end sm:items-center justify-end h-[30px] w-1/4 order-4 sm:order-2 mt-auto ml-auto text-nowrap"
              >
                <div
                  v-if="trip.price_min"
                  class="flex items-baseline"
                >
                  <small
                    v-if="isDiscounted"
                    class="mx-1 inline font-normal text-sm"
                    data-i18n="shortFromPrice"
                  >
                    {{ $t('shortFromPrice') }}
                  </small>
                  <small
                    v-if="isDiscounted"
                    class="strikethrough before-border-black mr-1 font-normal text-sm"
                  >
                    {{ $n(trip.price_min_without_discount) }}
                  </small>
                  <span
                    class="font-bold text-2xl -mt-1"
                    :class="{ 'text-discounted': isDiscounted }"
                  >
                    <small
                      v-if="!isDiscounted"
                      class="font-normal text-sm"
                    >
                      {{ $t('shortFromPrice') }}</small>{{ $n(trip.price_min) }}
                  </span>
                </div>
              </div>

              <div class="flex flex-col items-start relative w-3/4 sm:w-auto order-3 mb-2 mr-auto">
                <div class="flex flex-col">
                  <div class="flex items-center">
                    <fa :icon="trip?.flight_trip ? 'plane' : 'bus'" />
                    <span
                      v-if="days"
                      class="ml-2 font-inter"
                    >
                      {{ $t('daysText', { n: days }) }} </span>
                  </div>

                  <p class="font-inter text-base mb-1 h-min-px-45 flex items-center">
                    {{ trip?.subtitle }}
                  </p>
                </div>

                <div class="flex justify-start items-center">
                  <div
                    v-if="trip?.reviews_average && trip?.reviews_average.avg"
                    class="ml-0 flex items-center"
                  >
                    <LazyStars
                      class="pr-2"
                      :score="trip?.reviews_average.avg"
                      classes="product-price-box__stars"
                    />
                  </div>
                  <span class="pr-0 font-inter text-nowrap">
                    {{ $t('departuresText', trip?.departures) }}
                  </span>
                </div>
              </div>

              <div
                v-if="partnerLogoUrl"
                class="relative order-4 min-w-44 grow"
              >
                <img
                  class="h-full absolute object-contain left-0 sm:left-auto sm:right-0 w-44"
                  :src="partnerLogoUrl"
                  :alt="$t('siteTitleBrand')"
                  loading="lazy"
                />
              </div>

              <div class="flex flex-col text-center justify-center order-5 w-full">
                <div class="flex items-center justify-between">
                  <div
                    v-if="trip.price_min"
                    class="sm:hidden inline-flex text-nowrap"
                  >
                    <div class="flex flex-col items-start leading-tight">
                      <div
                        v-if="isDiscounted"
                        class="flex items-baseline"
                      >
                        <small
                          class="mx-1 font-normal text-sm"
                          data-i18n="shortFromPrice"
                        >
                          {{ $t('shortFromPrice') }}
                        </small>
                        <small class="strikethrough before-border-black mr-1 font-normal text-sm">
                          {{ $n(trip.price_min_without_discount) }}
                        </small>
                      </div>

                      <span
                        class="font-bold text-2xl"
                        :class="{ 'text-discounted': isDiscounted }"
                      >
                        <small
                          v-if="!isDiscounted"
                          class="font-normal text-sm"
                        >
                          {{ $t('shortFromPrice') }}
                        </small>
                        {{ $n(trip.price_min) }}
                      </span>
                    </div>
                  </div>
                  <nuxt-link
                    class="trip-card-button btn-secondary flex text-center ml-auto mt-4 sm:mt-auto text-nowrap px-4 py-1 font-semibold"
                    :to="trip?.url || '#'"
                  >
                    <span class="hidden sm:block">
                      {{ $t('readMoreAndBook') }}
                    </span>
                    <span class="sm:hidden">
                      {{ $t('readMore') }}
                    </span>
                    <fa
                      class="pl-1 mt-1"
                      icon="arrow-right"
                    />
                  </nuxt-link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </article>
  </div>
</template>

<script>
export default defineNuxtComponent({
  middleware: 'auth',

  auth: false,

  props: {
    trip: {
      type: Object,
      default: null,
    },

    loading: {
      type: Boolean,
      default: false,
    },
  },

  data () {
    return {
      contentContainerHeightPx: null,
      isHovering: false,
    }
  },

  methods: {
    updateContentContainerHeight () {
      if (!this.$refs.contentContainerRef) {
        return
      }

      this.contentContainerHeightPx = this.$refs.contentContainerRef.offsetHeight
    },
  },

  mounted () {
    this.updateContentContainerHeight()

    window.addEventListener('resize', this.updateContentContainerHeight)
  },

  beforeUnmount () {
    window.removeEventListener('resize', this.updateContentContainerHeight)
  },

  watch: {
    isHovering () {
      this.updateContentContainerHeight()
    },
  },

  computed: {
    days () {
      const daysMin = this.trip?.days_min || 0
      const daysMax = this.trip?.days_max

      return daysMin === daysMax ? daysMin : `${daysMin} – ${daysMax}`
    },

    partnerLogoUrl () {
      return this.trip?.partner_logo ? formatCloudinaryImagePath(this.trip?.partner_logo, {
        transformation: { custom: 'w_350,c_thumb' },
        isSol: this.$isSol,
      }) : null
    },

    isDiscounted () {
      return this.trip?.price_min < this.trip?.price_min_without_discount
    },
  },
})
</script>

<style lang="scss" scoped>
.right-align {
  left: unset;
  right: 0 !important;
  border-radius: 0 8px;
}

.trip-banner-background {
  background-image: url('/trip-cards-banner.svg');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}

.trip-card-container {
  min-height: 140px;

  .content-container {
    z-index: 1;

    @media (max-width: theme('screens.sm')) {
      height: auto !important;
    }
  }
}
</style>